import { Box, Chip, Skeleton, Typography } from "@mui/material"
import type { FC } from "react"
import { useEffect, useMemo, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useShallow } from "zustand/shallow"

import { SalesOrderLinePriceOverrideDialog } from "./SalesOrderLinePriceOverrideDialog"

import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import { PriceUomDisplay } from "@/components/PriceUomDisplay/PriceUomDisplay"
import { type RoundOffResult, RoundOffWarning } from "@/components/RoundOffWarning"
import { FormDatePicker } from "@/components/UiKit/FormControls/FormDatePicker"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { UOM_RENDER_PRECISION } from "@/constants/formats"
import { ComputedProductStatus, type Product, type SalesOrderPricingTotalsLineResult } from "@/graphql/codegen/graphql"
import { useRoundOffWarning } from "@/hooks/useRoundOffWarning"
import { ComputedProductStatusMap } from "@/screens/Companies/products/utils/productStatusMap"
import { useSalesOrderPermissions } from "@/screens/CustomerOrders/hooks/useSalesOrderPermissions"
import type { SalesOrderLogisticsFormValues } from "@/screens/CustomerOrders/schemas"
import type { LinkedQuantityField } from "@/screens/CustomerOrders/utils"
import { calculateLinkedQuantities } from "@/screens/CustomerOrders/utils"
import { PRODUCT_STOCK_TYPE_CHIP_COLOR, PRODUCT_STOCK_TYPE_LABEL_MAP } from "@/screens/Products/constants"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import type { Option } from "@/types/form-fields"
import { RoundPrecisionUnit } from "@/types/rounding"
import { createUomsList } from "@/utils/conversion"
import { formatNumber } from "@/utils/number"
import { isBagsWholeNumber } from "@/utils/purchaseOrder"
import { isNil } from "@/utils/utils"

interface SalesOrderLineProps {
  index: number
  cpoNumber: string
  product: Product
  lineTotals?: SalesOrderPricingTotalsLineResult
  isPricingFetching?: boolean
  readOnly?: boolean
}

export const SalesOrderLine: FC<SalesOrderLineProps> = ({
  index,
  cpoNumber,
  product,
  lineTotals,
  isPricingFetching = false,
  readOnly = false,
}) => {
  const [isPriceOverrideOpen, setIsPriceOverrideOpen] = useState(false)

  const { isReadonly: isPermissionReadonly } = useSalesOrderPermissions()
  const isReadonly = isPermissionReadonly || readOnly
  const { control, getValues, setValue } = useFormContext<SalesOrderLogisticsFormValues>()

  const basePath = `lines.${index}` as const
  const line = useWatch({ control, name: basePath })
  const { isVisible: isRoundOffVisible, evaluate: evaluateRoundOff, hide: hideRoundOff } = useRoundOffWarning()

  const { units, getUnits } = useUnitOfMeasurementsStore(
    useShallow((state) => ({ units: state.units, getUnits: state.getUnits }))
  )
  useEffect(() => {
    if (!units.length) getUnits()
  }, [units.length, getUnits])

  const { packageUsWeightUom, packageMetricWeightUom } = product

  const uomOptions: Option<string>[] = useMemo(
    () =>
      createUomsList(units, { packageUsWeightUom, packageMetricWeightUom }).map((uom) => ({
        value: uom.id,
        label: uom.name ?? uom.id,
      })),
    [units, packageUsWeightUom, packageMetricWeightUom]
  )

  const syncLinkedQuantities = (editedField: LinkedQuantityField, uomId: string | null) => {
    const next = calculateLinkedQuantities({
      editedField,
      values: {
        soPallets: getValues(`${basePath}.pallets`),
        soUnits: getValues(`${basePath}.units`),
        quantity: getValues(`${basePath}.quantityInUom`),
      },
      soUom: uomId,
      conversion: {
        packageUsWeight: product.packageUsWeight ?? null,
        packageMetricWeight: product.packageMetricWeight ?? null,
        packageUsWeightUom: product.packageUsWeightUom ?? null,
        packageMetricWeightUom: product.packageMetricWeightUom ?? null,
        packagesPerPallet: product.packagesPerPallet ?? null,
      },
    })

    const nextByPath = {
      soPallets: [`${basePath}.pallets`, next.soPallets],
      soUnits: [`${basePath}.units`, next.soUnits],
      quantity: [`${basePath}.quantityInUom`, next.quantity],
    } as const

    Object.values(nextByPath).forEach(([path, value]) => {
      if (value === getValues(path)) return
      setValue(path, value, { shouldDirty: true, shouldValidate: true })
    })
  }

  const handleLinkedQuantityChange = (editedField: LinkedQuantityField, source: "event" | "prop") => {
    if (source === "prop") return
    syncLinkedQuantities(editedField, getValues(`${basePath}.uomId`))
    evaluateRoundOff(getValues(`${basePath}.pallets`))
  }

  const handlePalletsChange = (_value: number | null, source: "event" | "prop") =>
    handleLinkedQuantityChange("soPallets", source)
  const handleUnitsChange = (_value: number | null, source: "event" | "prop") =>
    handleLinkedQuantityChange("soUnits", source)
  const handleQuantityChange = (_value: number | null, source: "event" | "prop") =>
    handleLinkedQuantityChange("quantity", source)

  const handleUomChange = (value: string | null) => {
    if (isNil(getValues(`${basePath}.quantityInUom`))) return
    syncLinkedQuantities("quantity", value)
    evaluateRoundOff(getValues(`${basePath}.pallets`))
  }

  const handleRoundOff = (result: RoundOffResult | null) => {
    hideRoundOff()
    if (!result) return

    if (result.precision === RoundPrecisionUnit.Bag) {
      setValue(`${basePath}.quantityInUom`, result.quantity, { shouldDirty: true, shouldValidate: true })
      syncLinkedQuantities("quantity", getValues(`${basePath}.uomId`))
      return
    }

    setValue(`${basePath}.pallets`, result.pallets, { shouldDirty: true, shouldValidate: true })
    syncLinkedQuantities("soPallets", getValues(`${basePath}.uomId`))
  }

  const price = lineTotals?.price
  const isManualPrice = price?.isManual ?? false
  const resolvedPricePerUnit = price?.pricePerUnit ?? null
  const getUomLabel = (uomId: string | null | undefined) => units.find((uom) => uom.id === uomId)?.name ?? uomId
  const resolvedPriceUomLabel = getUomLabel(price?.uomId) ?? null
  const hasPriceTier = !isManualPrice && !!lineTotals?.pricingEffectiveDate && !isNil(resolvedPricePerUnit)
  const total = isNil(resolvedPricePerUnit) ? null : (lineTotals?.total.price ?? 0)
  const convertedPrice = lineTotals?.converted
  const convertedPriceUomLabel = getUomLabel(convertedPrice?.pricePerUnitUomId)
  const oppositeQuantity = lineTotals?.opposite?.quantity ?? null
  const oppositeUomLabel = getUomLabel(lineTotals?.opposite?.quantityUomId)

  const handlePriceOverrideSubmit = ({ pricePerUom }: { pricePerUom: number | null }) => {
    setValue(`${basePath}.pricePerUom`, pricePerUom, { shouldDirty: true })
    setValue(`${basePath}.priceUomId`, line.uomId, { shouldDirty: true })
    setValue(`${basePath}.isManualPriceSet`, true, { shouldDirty: true })
    setIsPriceOverrideOpen(false)
  }

  const showRoundOffWarning =
    isRoundOffVisible && !isReadonly && !!line.uomId && !isNil(line.pallets) && !isNil(line.quantityInUom)

  return (
    <Box>
      <Box className='flex items-start gap-6 py-6'>
        <FormDatePicker
          control={control}
          name={`${basePath}.deliverDate`}
          label='Deliver By Date'
          disabled={isReadonly}
          className='w-40'
          data-testid={`customer-orders.so-line-items.deliver-by-date-field-${index}`}
        />

        <Box className='flex w-28 flex-col'>
          <Typography variant='caption' className='mb-2 text-xs leading-[0.875rem] text-gray-700'>
            Customer PO
          </Typography>
          <Typography
            variant='body2'
            className='text-primary'
            data-testid={`customer-orders.so-line-items.customer-po-${index}`}
          >
            {cpoNumber}
          </Typography>
        </Box>

        <Box
          className='flex w-32 flex-col items-start gap-1'
          data-testid={`customer-orders.so-line-items.product-id-${index}`}
        >
          <Box className='flex items-center gap-2'>
            <StatusIndicator
              status={product.computedProductStatus ?? ComputedProductStatus.Inactive}
              map={ComputedProductStatusMap}
            />
            <Typography variant='body2' className='font-bold text-gray-700'>
              {product.publicId}
            </Typography>
          </Box>
          <DisplayAS400Id
            id={product.externalSystemId}
            data-testid={`customer-orders.so-line-items.as400-chip-${index}`}
          />
          {product.stockType && (
            <Chip
              label={PRODUCT_STOCK_TYPE_LABEL_MAP[product.stockType]}
              color={PRODUCT_STOCK_TYPE_CHIP_COLOR[product.stockType]}
              size='small'
              data-testid={`customer-orders.so-line-items.stock-chip-${index}`}
            />
          )}
        </Box>

        <Box className='flex w-48 flex-col gap-1' data-testid={`customer-orders.so-line-items.product-${index}`}>
          <Typography variant='body2' className='font-bold text-gray-800'>
            {product.companyProductDescription}
          </Typography>
          {product.vendorDescription && (
            <Typography variant='body2' className='text-gray-700'>
              {product.vendorDescription}
            </Typography>
          )}
        </Box>

        <FormNumericField
          control={control}
          name={`${basePath}.pallets`}
          label='Pallets'
          required
          onChange={handlePalletsChange}
          hideErrorMessage
          disabled={isReadonly}
          className='w-28'
          data-testid={`customer-orders.so-line-items.pallets-field-${index}`}
        />

        <FormNumericField
          control={control}
          name={`${basePath}.units`}
          label='Units'
          required
          onChange={handleUnitsChange}
          hideErrorMessage
          disabled={isReadonly}
          className='w-28'
          data-testid={`customer-orders.so-line-items.units-field-${index}`}
        />

        <Box className='flex w-28 flex-col gap-1'>
          <FormNumericField
            control={control}
            name={`${basePath}.quantityInUom`}
            label='Quantity'
            required
            onChange={handleQuantityChange}
            hideErrorMessage
            disabled={isReadonly}
            data-testid={`customer-orders.so-line-items.quantity-field-${index}`}
          />
          {isPricingFetching ? (
            <Skeleton
              variant='text'
              className='w-16'
              data-testid={`customer-orders.so-line-items.opposite-quantity-skeleton-${index}`}
            />
          ) : (
            !isNil(oppositeQuantity) && (
              <Typography
                variant='caption'
                className='text-gray-700'
                data-testid={`customer-orders.so-line-items.opposite-quantity-${index}`}
              >
                ({formatNumber(oppositeQuantity, UOM_RENDER_PRECISION)} {oppositeUomLabel})
              </Typography>
            )
          )}
        </Box>

        <FormSelect
          control={control}
          name={`${basePath}.uomId`}
          label='UoM'
          required
          onChange={handleUomChange}
          hideErrorMessage
          placeholder='Select...'
          options={uomOptions}
          disabled={isReadonly}
          className='w-28'
          data-testid={`customer-orders.so-line-items.uom-field-${index}`}
        />

        <Box className='flex flex-1 flex-col items-end gap-1'>
          <PriceUomDisplay
            price={resolvedPricePerUnit}
            priceUomLabel={resolvedPriceUomLabel}
            convertedPrice={convertedPrice?.pricePerUnit}
            convertedPriceUomLabel={convertedPriceUomLabel}
            total={total}
            isFetching={isPricingFetching}
            isManualPriceSet={isManualPrice}
            effectiveDate={hasPriceTier ? lineTotals?.pricingEffectiveDate : undefined}
            disabled={isReadonly}
            onEdit={() => setIsPriceOverrideOpen(true)}
            testIdSuffix={String(index)}
          />
        </Box>

        {isPriceOverrideOpen && (
          <SalesOrderLinePriceOverrideDialog
            pricePerUom={resolvedPricePerUnit ?? line.pricePerUom}
            onClose={() => setIsPriceOverrideOpen(false)}
            onSubmit={handlePriceOverrideSubmit}
          />
        )}
      </Box>

      {showRoundOffWarning && (
        <RoundOffWarning
          product={product}
          uomId={line.uomId!}
          pallets={line.pallets!}
          quantity={line.quantityInUom!}
          isPrecisionLocked={isBagsWholeNumber(product, line.quantityInUom!, line.uomId!)}
          onRound={handleRoundOff}
        />
      )}
    </Box>
  )
}
